import React, { useCallback, useEffect, useRef, useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import app from '../../firebaseConfig';
import { ApiResponse } from '../../Types/Common.type';
import { Order } from '../../Types/Order.type';
import './Transactions.css';
import TransactionsFilters from '../../components/TransactionsFilters/TransactionsFilters';
import TransactionsTable from '../../components/TransactionsTable/TransactionsTable';
import { PaginationFilters, PaginationRequest } from '../../Types/Pagination.type';
import { toast, ToastContainer } from 'react-toastify';
import { getTodayDateString } from '../../utils/date.utils';

const INITIAL_PAGE_SIZE = 10;

const TransactionsPage: React.FC = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState<PaginationFilters>(() => ({
    start_date: getTodayDateString(),
    end_date: getTodayDateString(),
  }));
  const [totalRowCount, setTotalRowCount] = useState<number>(0);
  const [lastSortKey, setLastSortKey] = useState<string | undefined>(undefined);
  const [pageSize, setPageSize] = useState<number>(INITIAL_PAGE_SIZE);
  const [pageIndex, setPageIndex] = useState<number>(0);

  const isInitialMount = useRef(true);
  
  const fetchOrders = useCallback(async (currentFilters: PaginationFilters, currentPageSize: number, currentPageIndex: number) => {
    setLoading(true);
    try {
      const functions = getFunctions(app);
      const getPaginatedOrders = httpsCallable<PaginationRequest, ApiResponse>(functions, 'getPaginatedOrdersBigQuery');
      const payload: PaginationRequest = {
        lastSortKey: currentPageIndex > 0 ? lastSortKey : undefined,
        filters: currentFilters,
        pageSize: currentPageSize,
      };
      const result = (await getPaginatedOrders(payload)).data;
      if (result.code === 200) {
        const fetchedOrders = result.data?.orders || [];
        const totalRowCount = result.data?.totalCount || 0;
        setTotalRowCount(totalRowCount);
        setOrders(fetchedOrders);
        const sortKey = fetchedOrders.length > 0 ? fetchedOrders[fetchedOrders.length - 1].sort_key : undefined;
        setLastSortKey(sortKey);
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      console.error('Error al recuperar transacciones', error);
      toast.error('Error al recuperar transacciones');
    } finally {
      setLoading(false);
    }
  }, [lastSortKey]);

  // Single useEffect to handle all data fetching
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      fetchOrders(filters, pageSize, pageIndex);
    } else {
      // For subsequent calls, use current state values
      fetchOrders(filters, pageSize, pageIndex);
    }
  }, [filters, pageSize, pageIndex]);


  const handleFilterSearch = useCallback((newFilters: PaginationFilters) => {
    setFilters(newFilters);
    setPageIndex(0);
    setLastSortKey(undefined);
  }, []);

  const handlePageSizeChange = useCallback((newPageSize: number) => {
    setPageSize(newPageSize);
    setPageIndex(0);
    setLastSortKey(undefined);
  }, []);

  const handlePageIndexChange = useCallback((newIndex: number) => {
    setPageIndex(newIndex);
  }, []);

  return (
    <div>
      {/* Header */}
      <div className="mainHeader">
        <div className="textContainer">
          <h2>Transacciones</h2>
          <p>
            Todo el detalle de tus transacciones, intentos de cobro y cobros
          </p>
        </div>
      </div>

      {/* TODO: Summaries */}

      {/* Filters */}
      <div className="filters">
        <TransactionsFilters
          onSearch={handleFilterSearch}
          isLoading={loading}
        />
      </div>

      {/* Table */}
      <TransactionsTable 
        isLoading={loading}
        orders={orders} 
        initialPageSize={pageSize}
        initialPageIndex={pageIndex}
        onPageSizeChange={handlePageSizeChange}
        onPageIndexChange={handlePageIndexChange}
        totalRowCount={totalRowCount}
      />
      
      <ToastContainer />
    </div>
  );
};

export default TransactionsPage;